/*
 * Copyright (c) 2022 OTB Africa. All rights reserved. <https://otbafrica.com>
 *
 * Created Date: Monday, April 25th 2022, 5:46:17 pm
 * Author: OTB Africa, developers@otbafrica.com
 *
 * Use of this source code is governed by the license that can be found in
 * the LICENSE file or at https://developers.otbafrica.com/licenses/
 */

import "theme/css/Users.css";
import { useParams } from "react-router-dom";
import Stack from "@mui/material/Stack";
import { useWorkerAPIForm } from "workers/services/WorkersAPI";
import { TabbedNavigator, APIItemDisplayWrapper } from "ui-widgets";
import { UserProfileHeader, UserAccountInformation, UserPersonalInformation } from "users";
import { WorkerAssignedTeam } from "workers/components/WorkerAssignedTeam";
import { WorkProfileEdit } from "workers/components/WorkProfileEdit";
import Skeleton from "@mui/material/Skeleton";
import { AutoSaveAPIForm } from "ui-widgets/forms";
import { useGlobalScope } from "core/providers";
import Alert from "@mui/material/Alert";

export const WorkerProfilePage = () => {
  const { workerId } = useParams();

  const { useWorkerAPIFormReturn, workerData, setWorkerData } = useWorkerAPIForm(workerId ? parseInt(workerId) : null);

  const {
    userContext: { loggedInUser },
  } = useGlobalScope();

  const editModeCondition = loggedInUser?.id === workerData?.id; // API rejects the requests if someone tries to edit an account that is not theirs

  return (
    <APIItemDisplayWrapper
      showLoader={useWorkerAPIFormReturn.isAPIFetchingEditItem}
      loaderType="custom"
      customProgressLoader={<WorkerProfilePageSkeleton />}
      httpResponseStatusCode={useWorkerAPIFormReturn.editFetchHTTPStatusCode}
      isAPIError={useWorkerAPIFormReturn.editFetchHasError}
    >
      <Stack className="os-main-page-container os-main-user-profile-container">
        {!editModeCondition ? (
          <Alert variant="filled" severity="info">
            {`${
              workerData?.registrationprofile ? workerData?.registrationprofile.firstname : "User"
            } can edit their details on any of the portals you have deployed. You can only edit information for your user account.`}
          </Alert>
        ) : null}
        <UserProfileHeader editModeCondition={editModeCondition} setUser={setWorkerData} user={workerData} />
        <div className="os-singleuser-information">
          <TabbedNavigator>
            <>Account Information</>
            <AutoSaveAPIForm
              showSubmitButton={false}
              showFeedbackAlerts={false}
              useAPIFormReturn={useWorkerAPIFormReturn}
            >
              <div className="os-profile-information-container">
                <UserAccountInformation editModeCondition={editModeCondition} user={workerData} />
              </div>
            </AutoSaveAPIForm>

            <>Personal Infromation</>
            <div className="os-profile-information-container">
              <UserPersonalInformation
                editModeCondition={editModeCondition}
                setUser={setWorkerData}
                registrationProfile={workerData?.registrationprofile}
              />
            </div>

            <>Work Profile</>
            <div className="os-profile-information-container">
              <WorkProfileEdit worker={workerData} editModeCondition={true} />
            </div>

            <>Teams</>
            <div className="os-profile-information-container">
              <WorkerAssignedTeam workerId={workerData?.id} />
            </div>
          </TabbedNavigator>
        </div>
      </Stack>
    </APIItemDisplayWrapper>
  );
};

export const WorkerProfilePageSkeleton = () => {
  return (
    <Stack spacing={2}>
      <Skeleton animation="wave" variant="rectangular" className="os-profile-header-container" />
      <Skeleton animation="wave" variant="rectangular" height={48} />
      <Skeleton animation="wave" variant="rectangular" height={200} />
    </Stack>
  );
};
